import { useAuth } from '../AuthContext';
import config from '../config';
import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { deriveUserType, deriveUpgradeReason } from '../utils/subscriptionState';

/**
 * Hook for checking user access to Pro features and subscription status
 * @returns {Object} Feature access methods and subscription info
 */
export function useFeatureAccess() {
  const { subscriptionStatus, isGrandfathered, complimentaryAccess, userDocError, currentUser } = useAuth();
  const [hasSubscriptionHistory, setHasSubscriptionHistory] = useState(false);
  // False when the history read failed: classification must resolve to
  // 'unknown' instead of 'never_subscribed' (which redirects to registration).
  const [historyKnown, setHistoryKnown] = useState(true);
  const [checkingHistory, setCheckingHistory] = useState(true);

  // Check subscription history using Firebase Extension data
  useEffect(() => {
    const checkSubscriptionHistory = async () => {
      if (!currentUser) {
        setHasSubscriptionHistory(false);
        setHistoryKnown(true);
        setCheckingHistory(false);
        return;
      }

      try {
        // Check if user has any subscription history in Firebase Extension data
        const subscriptionsRef = collection(db, 'customers', currentUser.uid, 'subscriptions');
        const subscriptionsSnap = await getDocs(subscriptionsRef);

        setHasSubscriptionHistory(!subscriptionsSnap.empty);
        setHistoryKnown(true);
      } catch (error) {
        console.error('Error checking subscription history:', error);
        // Unknown, not false: a failed read must never route into registration.
        setHasSubscriptionHistory(false);
        setHistoryKnown(false);
      } finally {
        setCheckingHistory(false);
      }
    };

    checkSubscriptionHistory();
  }, [currentUser]);

  /**
   * Check if user has access to a specific feature
   * @param {string} featureName - The feature name to check
   * @returns {boolean} Whether user has access to the feature
   */
  const hasAccess = useCallback((featureName) => {
    // If feature is not configured as Pro, it's free for everyone
    if (!config.FEATURES.IS_PRO_FEATURE[featureName]) {
      return true;
    }

    // Grandfathered users have access to all Pro features
    if (isGrandfathered) {
      return true;
    }

    // For Pro features, check subscription status
    const validStatuses = ['trialing', 'active'];
    return subscriptionStatus && validStatuses.includes(subscriptionStatus.status);
  }, [subscriptionStatus, isGrandfathered]);

  /**
   * Get current subscription status with grandfathering consideration
   * @returns {Object} Subscription status object
   */
  const getSubscriptionStatus = useCallback(() => {
    if (isGrandfathered) {
      return { status: 'grandfathered', tier: 'pro' };
    }
    return subscriptionStatus || { status: 'none', tier: 'free' };
  }, [subscriptionStatus, isGrandfathered]);

  /**
   * Check if user needs to upgrade for a specific feature
   * @param {string} featureName - The feature name to check
   * @returns {boolean} Whether user needs to upgrade
   */
  const requiresUpgrade = useCallback((featureName) => {
    return !hasAccess(featureName);
  }, [hasAccess]);

  /**
   * Get the reason why upgrade is needed
   * @returns {string} The upgrade reason ('unknown' when a required read
   *   failed — callers must show retry copy, never registration/payment UX)
   */
  const getUpgradeReason = useCallback(() => {
    return deriveUpgradeReason({
      isGrandfathered,
      subscriptionStatus,
      hasSubscriptionHistory,
      // A failed user-doc read means the grant/marker is unknown too.
      historyKnown: historyKnown && !userDocError,
      complimentaryExpired: complimentaryAccess?.expired === true,
    });
  }, [isGrandfathered, subscriptionStatus, hasSubscriptionHistory, historyKnown, userDocError, complimentaryAccess]);

  /**
   * Check if user is lapsed (had subscription but no longer active)
   * @returns {boolean} Whether user is lapsed
   */
  const isLapsed = useCallback(() => {
    if (isGrandfathered) return false;
    
    const validStatuses = ['trialing', 'active'];
    const hasActiveSubscription = subscriptionStatus && validStatuses.includes(subscriptionStatus.status);
    
    return hasSubscriptionHistory && !hasActiveSubscription;
  }, [isGrandfathered, subscriptionStatus, hasSubscriptionHistory]);

  /**
   * Check if user has never subscribed
   * @returns {boolean} Whether user has never subscribed
   */
  const isNeverSubscribed = useCallback(() => {
    if (isGrandfathered) return false;
    return !hasSubscriptionHistory && !complimentaryAccess?.expired;
  }, [isGrandfathered, hasSubscriptionHistory, complimentaryAccess]);

  /**
   * Check if the user's complimentary (grandfathered) access has ended.
   * False once any real Stripe lifecycle exists — Stripe history wins.
   * @returns {boolean}
   */
  const isComplimentaryExpired = useCallback(() => {
    return getUserTypeInternal() === 'complimentary_expired';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGrandfathered, subscriptionStatus, hasSubscriptionHistory, historyKnown, userDocError, complimentaryAccess]);

  const getUserTypeInternal = () => deriveUserType({
    isGrandfathered,
    subscriptionStatus,
    hasSubscriptionHistory,
    // A failed user-doc read means the grant/marker is unknown too.
    historyKnown: historyKnown && !userDocError,
    complimentaryExpired: complimentaryAccess?.expired === true,
  });

  /**
   * Get user classification for access control
   * @returns {string} 'grandfathered' | 'active' | 'lapsed' |
   *   'complimentary_expired' | 'never_subscribed' | 'unknown'
   *
   * 'unknown' means a required read failed — callers must stay on a
   * defensive path and never route to registration off it.
   */
  const getUserType = useCallback(() => {
    return getUserTypeInternal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGrandfathered, subscriptionStatus, hasSubscriptionHistory, historyKnown, userDocError, complimentaryAccess]);

  // Computed properties for easy access
  const isPro = isGrandfathered || subscriptionStatus?.tier === 'pro';
  const isTrialing = subscriptionStatus?.status === 'trialing';
  const isActive = subscriptionStatus?.status === 'active';
  const isFree = !isPro;

  return {
    // Core access methods
    hasAccess,
    requiresUpgrade,
    getSubscriptionStatus,
    getUpgradeReason,

    // User classification methods
    isLapsed,
    isNeverSubscribed,
    isComplimentaryExpired,
    getUserType,

    // Convenience properties
    isPro,
    isTrialing,
    isActive,
    isFree,
    isGrandfathered,
    // { expired, endedAt, until } from the user document's end-audit fields
    complimentaryAccess,

    // Subscription history
    hasSubscriptionHistory,
    checkingHistory,

    // Raw subscription data for advanced use cases
    subscriptionStatus,
  };
} 